import { useCallback, useEffect, useState } from 'react';
import {
  Loader,
  AlertCircle,
  Workflow,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
  CalendarClock,
  Power,
} from 'lucide-react';
import { format } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import type { EventWorkflow, EventWorkflowStage, EventWorkflowWithStages } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { computeStageDueDate } from '../ApplyWorkflowModal';
import { ConfirmationModal } from '../../ConfirmationModal';
import { CreatorProfile } from '../../CreatorProfile';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

interface DraftStage {
  title: string;
  description: string;
  default_role: string;
  offset_days: string;
}

const EMPTY_DRAFT_STAGE: DraftStage = { title: '', description: '', default_role: '', offset_days: '' };

const STAGE_ROLES = ['', 'host', 'organizer', 'helper', 'reviewer'];

export interface EventWorkflowsPanelProps {
  perms: EventTeamPerms;
}

/**
 * Workflow Editor (guide §1.9): reusable stage templates that instantiate assignable,
 * due-dated run-sheet tasks on events. `offset_days` is relative to the event's
 * `scheduled_start` (negative = days before). Fractional positions for reorder.
 */
export const EventWorkflowsPanel = ({ perms }: EventWorkflowsPanelProps) => {
  const [workflows, setWorkflows] = useState<EventWorkflow[]>([]);
  const [activeOnly, setActiveOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Expanded workflow detail
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<EventWorkflowWithStages | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busyStageId, setBusyStageId] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [draftStages, setDraftStages] = useState<DraftStage[]>([{ ...EMPTY_DRAFT_STAGE }]);
  const [creating, setCreating] = useState(false);

  // Edit workflow meta
  const [editingMeta, setEditingMeta] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  // Stage editor
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [stageDraft, setStageDraft] = useState<DraftStage>({ ...EMPTY_DRAFT_STAGE });
  const [showAddStage, setShowAddStage] = useState(false);
  const [addStageDraft, setAddStageDraft] = useState<DraftStage>({ ...EMPTY_DRAFT_STAGE });

  const [workflowToDelete, setWorkflowToDelete] = useState<EventWorkflow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Preview date for offset computation
  const [previewDate, setPreviewDate] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventTeamApiService.listWorkflows(activeOnly);
      setWorkflows(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const data = await eventTeamApiService.getWorkflow(id);
      data.stages.sort((a, b) => a.position - b.position);
      setDetail(data);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load workflow');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const toggleExpand = (workflow: EventWorkflow) => {
    if (expandedId === workflow.id) {
      setExpandedId(null);
      setDetail(null);
      setEditingMeta(false);
      return;
    }
    setExpandedId(workflow.id);
    setDetail(null);
    setEditingMeta(false);
    setShowAddStage(false);
    void loadDetail(workflow.id);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const stages = draftStages
        .filter((s) => s.title.trim())
        .map((s) => ({
          title: s.title.trim(),
          description: s.description.trim() || null,
          default_role: s.default_role || null,
          offset_days: s.offset_days.trim() !== '' ? Number(s.offset_days) : null,
        }));
      await eventTeamApiService.createWorkflow({
        name: newName.trim(),
        description: newDescription.trim() || null,
        category: newCategory.trim() || null,
        stages: stages.length > 0 ? stages : undefined,
      });
      setNewName('');
      setNewDescription('');
      setNewCategory('');
      setDraftStages([{ ...EMPTY_DRAFT_STAGE }]);
      setShowCreate(false);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create workflow');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (workflow: EventWorkflow) => {
    try {
      await eventTeamApiService.updateWorkflow(workflow.id, { is_active: workflow.is_active !== 1 });
      await load();
      if (expandedId === workflow.id) await loadDetail(workflow.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle workflow');
    }
  };

  const handleSaveMeta = async () => {
    if (!detail || !editName.trim()) return;
    setSavingMeta(true);
    try {
      await eventTeamApiService.updateWorkflow(detail.id, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        category: editCategory.trim() || null,
      });
      setEditingMeta(false);
      await load();
      await loadDetail(detail.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update workflow');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;
    setDeleting(true);
    try {
      await eventTeamApiService.deleteWorkflow(workflowToDelete.id);
      setWorkflowToDelete(null);
      if (expandedId === workflowToDelete.id) {
        setExpandedId(null);
        setDetail(null);
      }
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete workflow');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddStage = async () => {
    if (!detail || !addStageDraft.title.trim()) return;
    setBusyStageId('new');
    try {
      const maxPos = detail.stages.reduce((max, s) => Math.max(max, s.position), 0);
      await eventTeamApiService.addWorkflowStage(detail.id, {
        title: addStageDraft.title.trim(),
        description: addStageDraft.description.trim() || null,
        default_role: addStageDraft.default_role || null,
        offset_days: addStageDraft.offset_days.trim() !== '' ? Number(addStageDraft.offset_days) : null,
        position: maxPos + 1,
      });
      setAddStageDraft({ ...EMPTY_DRAFT_STAGE });
      setShowAddStage(false);
      await loadDetail(detail.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add stage');
    } finally {
      setBusyStageId(null);
    }
  };

  const handleSaveStage = async (stage: EventWorkflowStage) => {
    if (!detail || !stageDraft.title.trim()) return;
    setBusyStageId(stage.id);
    try {
      await eventTeamApiService.updateWorkflowStage(stage.id, {
        title: stageDraft.title.trim(),
        description: stageDraft.description.trim() || null,
        default_role: stageDraft.default_role || null,
        offset_days: stageDraft.offset_days.trim() !== '' ? Number(stageDraft.offset_days) : null,
      });
      setEditingStageId(null);
      await loadDetail(detail.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update stage');
    } finally {
      setBusyStageId(null);
    }
  };

  const handleDeleteStage = async (stage: EventWorkflowStage) => {
    if (!detail || !confirm(`Delete stage "${stage.title}"?`)) return;
    setBusyStageId(stage.id);
    try {
      await eventTeamApiService.deleteWorkflowStage(stage.id);
      await loadDetail(detail.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete stage');
    } finally {
      setBusyStageId(null);
    }
  };

  /** Fractional `position` midpoints — drag-reorder semantics via up/down buttons. */
  const handleMoveStage = async (stage: EventWorkflowStage, direction: -1 | 1) => {
    if (!detail) return;
    const stages = detail.stages;
    const idx = stages.findIndex((s) => s.id === stage.id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= stages.length) return;
    const neighbour = stages[targetIdx];
    const beyond = stages[targetIdx + direction];
    const newPos = beyond ? (neighbour.position + beyond.position) / 2 : neighbour.position + direction;
    setBusyStageId(stage.id);
    try {
      await eventTeamApiService.updateWorkflowStage(stage.id, { position: newPos });
      await loadDetail(detail.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reorder stage');
    } finally {
      setBusyStageId(null);
    }
  };

  const previewMs = previewDate ? new Date(previewDate).getTime() : null;

  const stageDraftEditor = (
    draft: DraftStage,
    setDraft: (d: DraftStage) => void,
    onCancel: () => void,
    onSave: () => void,
    saving: boolean,
  ) => (
    <div className="subcard p-3 space-y-2">
      <input
        type="text"
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        placeholder="Stage title (e.g. Write announcement)"
        className="glass-input w-full text-sm"
        autoFocus
      />
      <input
        type="text"
        value={draft.description}
        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        placeholder="Description (optional)"
        className="glass-input w-full text-sm"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={draft.default_role}
          onChange={(e) => setDraft({ ...draft, default_role: e.target.value })}
          className="glass-select text-sm capitalize"
        >
          {STAGE_ROLES.map((r) => (
            <option key={r || 'none'} value={r}>
              {r || 'No default role'}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={draft.offset_days}
          onChange={(e) => setDraft({ ...draft, offset_days: e.target.value })}
          placeholder="Offset days"
          className="glass-input text-sm w-32"
          title="Days relative to the event's scheduled start (-7 = a week before)"
        />
        <span className="text-[10px] text-glass-500">negative = days before start</span>
        <div className="flex-1" />
        <button type="button" onClick={onCancel} className="btn-icon btn-icon-sm" title="Cancel" aria-label="Cancel">
          <X className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !draft.title.trim()}
          className="btn-icon btn-icon-sm text-green-400"
          title="Save stage"
          aria-label="Save stage"
        >
          {saving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Workflow Templates"
        icon={Workflow}
        iconClassName="text-violet-400"
        count={workflows.length}
        description="Reusable stage templates that become due-dated run-sheet tasks on events."
        className="mb-0 flex-wrap"
        actions={
          <>
            <label className="flex items-center gap-2 text-xs text-glass-400 cursor-pointer">
              <span className="switch">
                <input
                  type="checkbox"
                  checked={activeOnly}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                />
                <span />
              </span>
              Active only
            </label>
            {perms.canLead && (
              <button
                type="button"
                onClick={() => setShowCreate(!showCreate)}
                className="glass-button"
                aria-expanded={showCreate}
              >
                <Plus className="w-4 h-4" />
                New workflow
              </button>
            )}
          </>
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {/* Create form with inline stages */}
      {showCreate && perms.canLead && (
        <div className="glass-card p-4 md:p-5 space-y-3">
          <SectionHeader title="New workflow template" className="mb-0" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Name (e.g. Standard event run-sheet) *"
              className="glass-input text-sm md:col-span-2"
              autoFocus
            />
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Category (optional)"
              className="glass-input text-sm"
            />
          </div>
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (optional)"
            className="glass-input w-full text-sm min-h-[50px]"
          />
          <div className="space-y-2">
            <p className="eyebrow">Stages (in order)</p>
            {draftStages.map((stage, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-glass-500 text-sm pt-2 w-5 text-right">{i + 1}.</span>
                <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2">
                  <input
                    type="text"
                    value={stage.title}
                    onChange={(e) =>
                      setDraftStages((prev) => prev.map((s, j) => (j === i ? { ...s, title: e.target.value } : s)))
                    }
                    placeholder="Stage title"
                    className="glass-input text-sm md:col-span-2"
                  />
                  <select
                    value={stage.default_role}
                    onChange={(e) =>
                      setDraftStages((prev) => prev.map((s, j) => (j === i ? { ...s, default_role: e.target.value } : s)))
                    }
                    className="glass-select text-sm capitalize"
                  >
                    {STAGE_ROLES.map((r) => (
                      <option key={r || 'none'} value={r}>
                        {r || 'No role'}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    value={stage.offset_days}
                    onChange={(e) =>
                      setDraftStages((prev) => prev.map((s, j) => (j === i ? { ...s, offset_days: e.target.value } : s)))
                    }
                    placeholder="Offset days"
                    className="glass-input text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setDraftStages((prev) => prev.filter((_, j) => j !== i))}
                  disabled={draftStages.length === 1}
                  className="btn-icon btn-icon-sm mt-1.5 text-red-400 hover:text-red-300 disabled:opacity-30"
                  title="Remove stage"
                  aria-label="Remove stage"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setDraftStages((prev) => [...prev, { ...EMPTY_DRAFT_STAGE }])}
              className="glass-button-secondary btn-xs"
            >
              <Plus className="w-3 h-3" />
              Add stage
            </button>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={() => setShowCreate(false)} className="glass-button-secondary">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={creating || !newName.trim()}
              className="btn-primary"
            >
              {creating ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create workflow
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="glass-card p-3 flex items-center gap-3">
              <SkeletonLoader className="h-4 w-4" />
              <div className="flex-1 space-y-1.5">
                <SkeletonLoader className="h-4 w-48 max-w-full" />
                <SkeletonLoader className="h-3 w-72 max-w-full" />
              </div>
              <SkeletonLoader className="h-5 w-14 rounded-full" />
            </div>
          ))}
        </div>
      ) : workflows.length === 0 && !error ? (
        <EmptyState
          icon={Workflow}
          title="No workflow templates"
          description={perms.canLead ? 'Create a template to instantiate run-sheet tasks on events.' : undefined}
        />
      ) : (
        <div className="space-y-2">
          {workflows.map((workflow) => {
            const expanded = expandedId === workflow.id;
            return (
              <div key={workflow.id} className="glass-card overflow-visible">
                <div className="flex items-center justify-between gap-2 p-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => toggleExpand(workflow)}
                    className="flex items-center gap-2 min-w-0 text-left flex-1"
                    aria-expanded={expanded}
                  >
                    {expanded ? (
                      <ChevronDown className="w-4 h-4 text-glass-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-glass-400 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <span className="text-sm font-semibold text-white truncate block">{workflow.name}</span>
                      {workflow.description && (
                        <span className="text-xs text-glass-400 line-clamp-1">{workflow.description}</span>
                      )}
                    </div>
                    {workflow.category && (
                      <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25 flex-shrink-0">
                        {workflow.category}
                      </span>
                    )}
                    <span
                      className={cn(
                        'badge badge-dot flex-shrink-0',
                        workflow.is_active === 1 ? 'badge-success' : 'badge-neutral',
                      )}
                    >
                      {workflow.is_active === 1 ? 'active' : 'inactive'}
                    </span>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {perms.canLead && (
                      <button
                        type="button"
                        onClick={() => void handleToggleActive(workflow)}
                        className={cn(
                          'btn-icon btn-icon-sm',
                          workflow.is_active === 1 ? 'text-green-300' : 'text-glass-400',
                        )}
                        title={workflow.is_active === 1 ? 'Deactivate' : 'Activate'}
                        aria-label={workflow.is_active === 1 ? 'Deactivate workflow' : 'Activate workflow'}
                      >
                        <Power className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {perms.canAdmin && (
                      <button
                        type="button"
                        onClick={() => setWorkflowToDelete(workflow)}
                        className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                        title="Delete workflow (admin; already-instantiated tasks survive)"
                        aria-label="Delete workflow"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {expanded && (
                  <div className="border-t border-glass-200/10 p-3 space-y-3">
                    {detailLoading || !detail ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader className="w-5 h-5 animate-spin text-accent-400" />
                      </div>
                    ) : (
                      <>
                        {/* Meta editor */}
                        {perms.canLead &&
                          (editingMeta ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  className="glass-input text-sm md:col-span-2"
                                />
                                <input
                                  type="text"
                                  value={editCategory}
                                  onChange={(e) => setEditCategory(e.target.value)}
                                  placeholder="Category"
                                  className="glass-input text-sm"
                                />
                              </div>
                              <textarea
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Description"
                                className="glass-input w-full text-sm min-h-[40px]"
                              />
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingMeta(false)}
                                  className="btn-icon btn-icon-sm"
                                  title="Cancel"
                                  aria-label="Cancel editing"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleSaveMeta()}
                                  disabled={savingMeta || !editName.trim()}
                                  className="btn-icon btn-icon-sm text-green-400"
                                  title="Save"
                                  aria-label="Save workflow details"
                                >
                                  {savingMeta ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setEditingMeta(true);
                                setEditName(detail.name);
                                setEditDescription(detail.description ?? '');
                                setEditCategory(detail.category ?? '');
                              }}
                              className="glass-button-secondary btn-xs"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                              Edit name / description
                            </button>
                          ))}

                        {/* Due-date preview control */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-glass-400 inline-flex items-center gap-1">
                            <CalendarClock className="w-3.5 h-3.5 text-cyan-400" />
                            Preview due dates for a hypothetical event start:
                          </span>
                          <input
                            type="datetime-local"
                            value={previewDate}
                            onChange={(e) => setPreviewDate(e.target.value)}
                            className="glass-input text-xs"
                          />
                          {previewDate && (
                            <button
                              type="button"
                              onClick={() => setPreviewDate('')}
                              className="btn-icon btn-icon-sm"
                              title="Clear preview date"
                              aria-label="Clear preview date"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>

                        {/* Stages */}
                        <div className="space-y-1.5">
                          {detail.stages.length === 0 && (
                            <p className="text-xs text-glass-500 py-2 text-center">No stages.</p>
                          )}
                          {detail.stages.map((stage, idx) => {
                            const busy = busyStageId === stage.id;
                            const isEditing = editingStageId === stage.id;
                            const due = previewMs != null ? computeStageDueDate(previewMs, stage.offset_days) : null;
                            if (isEditing) {
                              return (
                                <div key={stage.id}>
                                  {stageDraftEditor(
                                    stageDraft,
                                    setStageDraft,
                                    () => setEditingStageId(null),
                                    () => void handleSaveStage(stage),
                                    busy,
                                  )}
                                </div>
                              );
                            }
                            return (
                              <div
                                key={stage.id}
                                className="subcard flex items-center justify-between gap-2 px-3 py-2 flex-wrap"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                  <span className="text-glass-500 text-sm w-5 text-right flex-shrink-0">{idx + 1}.</span>
                                  <span className="text-sm text-white">{stage.title}</span>
                                  {stage.default_role && (
                                    <span className="badge badge-accent capitalize">{stage.default_role}</span>
                                  )}
                                  {stage.offset_days != null && (
                                    <span className="text-[11px] text-cyan-300 tabular-nums">
                                      {stage.offset_days >= 0 ? '+' : ''}
                                      {stage.offset_days}d
                                      {due != null && ` → ${format(new Date(due), 'MMM d, HH:mm')}`}
                                    </span>
                                  )}
                                  {stage.description && (
                                    <span className="text-[11px] text-glass-500 italic truncate max-w-[260px]">
                                      {stage.description}
                                    </span>
                                  )}
                                </div>
                                {perms.canLead && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <div className="flex flex-col">
                                      <button
                                        onClick={() => void handleMoveStage(stage, -1)}
                                        disabled={busy || idx === 0}
                                        className="text-glass-500 hover:text-glass-200 disabled:opacity-30 p-0.5 rounded"
                                        title="Move up"
                                        aria-label="Move stage up"
                                      >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => void handleMoveStage(stage, 1)}
                                        disabled={busy || idx === detail.stages.length - 1}
                                        className="text-glass-500 hover:text-glass-200 disabled:opacity-30 p-0.5 rounded"
                                        title="Move down"
                                        aria-label="Move stage down"
                                      >
                                        <ChevronDownIcon className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setEditingStageId(stage.id);
                                        setStageDraft({
                                          title: stage.title,
                                          description: stage.description ?? '',
                                          default_role: stage.default_role ?? '',
                                          offset_days: stage.offset_days != null ? String(stage.offset_days) : '',
                                        });
                                      }}
                                      disabled={busy}
                                      className="btn-icon btn-icon-sm"
                                      title="Edit stage"
                                      aria-label="Edit stage"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => void handleDeleteStage(stage)}
                                      disabled={busy}
                                      className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                                      title="Delete stage"
                                      aria-label="Delete stage"
                                    >
                                      {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {perms.canLead &&
                          (showAddStage ? (
                            stageDraftEditor(
                              addStageDraft,
                              setAddStageDraft,
                              () => setShowAddStage(false),
                              () => void handleAddStage(),
                              busyStageId === 'new',
                            )
                          ) : (
                            <button
                              type="button"
                              onClick={() => setShowAddStage(true)}
                              className="glass-button-secondary btn-xs"
                            >
                              <Plus className="w-3 h-3" />
                              Add stage
                            </button>
                          ))}

                        <p className="text-[11px] text-glass-500 inline-flex items-center gap-1 flex-wrap">
                          Created by <CreatorProfile creatorId={detail.created_by} inline />
                          {detail.updated_by && (
                            <>
                              {' '}
                              · updated by <CreatorProfile creatorId={detail.updated_by} inline />
                            </>
                          )}
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {workflowToDelete && (
        <ConfirmationModal
          isOpen={!!workflowToDelete}
          onClose={() => setWorkflowToDelete(null)}
          onConfirm={() => void handleDelete()}
          title="Delete Workflow"
          message={`Delete "${workflowToDelete.name}"? Stages are removed with it, but tasks already instantiated on events survive.`}
          confirmText="Delete"
          isLoading={deleting}
        />
      )}
    </div>
  );
};
